import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { X, FileText, AlertCircle, CheckCircle2, Loader2, Upload } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function fmtMoney(v, currency) {
  const n = Number(v) || 0;
  return `${n.toFixed(2)} ${currency || "RON"}`;
}

export default function InvoicePreviewModal({ order, open, onClose, onIssued }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emitting, setEmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open || !order) return;
    let cancelled = false;
    (async () => {
      setLoading(true); setError(""); setPreview(null);
      try {
        // Must send `integration_key` so the backend knows how to look up
        // the order — without it, non-eMAG orders (Gomag, Trendyol, manual)
        // hit the default eMAG lookup path and 404 with "Comanda nu există
        // în cache". `order._integration_key` is the canonical field
        // returned by /api/orders for every source.
        const integ_key = order._integration_key || order.integration_key;
        const r = await axios.post(`${API}/orders/${order.id}/invoice`, {
          dry_run: true,
          integration_key: integ_key,
        });
        if (!cancelled) setPreview(r.data);
      } catch (e) {
        if (!cancelled) {
          // User cerinta Oct 2026: eroare dedicata „CUI lipsa" pe firma —
          // mesaj clar + hint ca sa editeze CUI-ul in tab-ul Adresa facturare.
          const detail = e.response?.data?.detail;
          if (detail && typeof detail === "object" && detail.error_code === "cif_missing") {
            setError(detail.message);
          } else {
            setError(detail || e.message);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, order?.id]);

  if (!open || !order) return null;

  const handleEmit = async () => {
    setEmitting(true); setError("");
    try {
      const integ_key = order._integration_key || order.integration_key;
      const r = await axios.post(`${API}/orders/${order.id}/invoice`, {
        integration_key: integ_key,
      });
      const invUrl = r.data?.url || r.data?.invoice_url;
      const invNum = r.data?.number || r.data?.invoice_number || "OK";
      if (invUrl && order) {
        // Update in-memory order so the row reflects the new state immediately.
        // (We intentionally don't open the PDF in a new tab — the user can
        //  click the invoice link from the order row when they want it.)
        order.invoice_url = invUrl; order.invoice_number = invNum;
      }
      onIssued?.({ url: invUrl, number: invNum });
      onClose();
    } catch (e) {
      // Idem — eroare dedicata „CUI lipsa" (user cerinta Oct 2026).
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error_code === "cif_missing") {
        setError(detail.message);
      } else {
        setError(detail || e.message);
      }
    } finally { setEmitting(false); }
  };

  // Upload PDF manual — comportament identic cu emiterea nativă:
  // trimite emailuri către client, push în Shopify/Gomag, finalizare comandă.
  const handleUpload = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") { setError("Doar fișiere PDF."); return; }
    const number = (window.prompt("Număr factură (ex: BRAG 00123):", order.invoice_number || "") || "").trim();
    setUploading(true); setError("");
    try {
      const integ_key = order._integration_key || order.integration_key;
      const fd = new FormData();
      fd.append("file", file);
      fd.append("number", number);
      if (integ_key) fd.append("integration_key", integ_key);
      const r = await axios.post(`${API}/orders/${order.id}/invoice/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const invUrl = r.data?.invoice_url;
      const invNum = r.data?.invoice_number;
      if (invUrl && order) {
        order.invoice_url = invUrl;
        order.invoice_number = invNum;
      }
      onIssued?.({ url: invUrl, number: invNum });
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  // The backend dry_run returns the FULL Oblio payload schema:
  //   { preview, platform, cif, series, management, client, products, currency, totals, payload }
  // `products` mixes real product lines AND discount/voucher lines (a discount line has a `discount` field).
  // We render them in two tbody groups so vouchers stay visible.
  const products = preview?.products || [];
  const productLines = products.filter((p) => !("discount" in p));
  const discountLines = products.filter((p) => "discount" in p);
  const client = preview?.client || {};
  const currency = preview?.currency || order?.currency || "RON";
  const totals = preview?.totals || {};

  return (
    <div data-testid="invoice-preview-modal" className="fixed inset-0 z-[70] flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center sm:p-4">
      <div className="flex max-h-[95vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-2xl border border-zinc-800 bg-zinc-950 shadow-2xl sm:rounded-2xl">
        <div className="flex items-center justify-between border-b border-zinc-800 bg-zinc-900/60 px-4 py-3">
          <div className="flex items-center gap-2 text-sm">
            <FileText className="h-4 w-4 text-emerald-300" />
            <span className="font-semibold text-zinc-100">Previzualizare factură Oblio</span>
            <span className="rounded bg-orange-500/10 px-1.5 py-0.5 font-mono text-[10px] text-orange-200">#{order.id}</span>
            {preview?.series && (
              <span className="rounded bg-zinc-800 px-1.5 py-0.5 font-mono text-[10px] text-zinc-300">{preview.series}</span>
            )}
          </div>
          <button data-testid="invoice-preview-close" onClick={onClose} className="rounded-md p-1.5 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200"><X className="h-4 w-4" /></button>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto p-4">
          {loading && (
            <div className="flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900/40 px-3 py-2.5 text-xs text-zinc-400">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Se calculează factura...
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" /><span>{error}</span>
            </div>
          )}

          {preview && (
            <>
              <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3 text-xs">
                <p className="mb-1 text-[10px] uppercase tracking-wider text-zinc-500">Client</p>
                <p className="font-medium text-zinc-100">{client.name || "—"}</p>
                <p className="text-zinc-400">
                  {[client.address, client.city, client.state, client.country].filter(Boolean).join(", ") || "—"}
                </p>
                {client.email && <p className="text-zinc-500">{client.email}</p>}
                {client.phone && <p className="text-zinc-500">{client.phone}</p>}
                {(client.cif || client.cnp) && <p className="text-zinc-500">{client.cif || client.cnp}</p>}
              </div>

              <div className="overflow-hidden rounded-lg border border-zinc-800">
                <table className="w-full text-xs">
                  <thead className="bg-zinc-900/60 text-[10px] uppercase tracking-wider text-zinc-500">
                    <tr>
                      <th className="px-3 py-2 text-left">Produs</th>
                      <th className="px-3 py-2 text-right">Cant.</th>
                      <th className="px-3 py-2 text-right">UM</th>
                      <th className="px-3 py-2 text-right">Preț</th>
                      <th className="px-3 py-2 text-right">TVA%</th>
                      <th className="px-3 py-2 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-900">
                    {productLines.map((p, i) => {
                      const qty = Number(p.quantity) || 0;
                      const price = Number(p.price) || 0;
                      const subtotal = qty * price;
                      return (
                        <tr key={`p${i}`} className="text-zinc-200">
                          <td className="px-3 py-2 align-top">
                            <span className="line-clamp-2">{p.name}</span>
                            {p.code && <p className="text-[10px] font-mono text-zinc-500">{p.code}</p>}
                          </td>
                          <td className="px-3 py-2 text-right">{qty}</td>
                          <td className="px-3 py-2 text-right text-zinc-500">{p.measuringUnit || "buc"}</td>
                          <td className="px-3 py-2 text-right font-mono">{fmtMoney(price, currency)}</td>
                          <td className="px-3 py-2 text-right text-zinc-500">{p.vatPercentage ?? "—"}{p.vatIncluded ? " (inc)" : ""}</td>
                          <td className="px-3 py-2 text-right font-mono">{fmtMoney(subtotal, currency)}</td>
                        </tr>
                      );
                    })}
                    {discountLines.map((d, i) => (
                      <tr key={`d${i}`} className="bg-emerald-500/5 text-emerald-200">
                        <td colSpan={5} className="px-3 py-1.5 align-top text-left">
                          <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider">Discount</span>
                          <span className="ml-2">{d.name || "Voucher"}</span>
                          {d.discountType && <span className="ml-1 text-[10px] text-zinc-500">({d.discountType})</span>}
                        </td>
                        <td className="px-3 py-1.5 text-right font-mono">-{fmtMoney(d.discount, currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-zinc-900/40 text-zinc-100">
                    <tr>
                      <td colSpan={5} className="px-3 py-1.5 text-right text-zinc-400">Net</td>
                      <td data-testid="invoice-preview-net" className="px-3 py-1.5 text-right font-mono">{fmtMoney(totals.net, currency)}</td>
                    </tr>
                    <tr>
                      <td colSpan={5} className="px-3 py-1.5 text-right text-zinc-400">TVA</td>
                      <td data-testid="invoice-preview-vat" className="px-3 py-1.5 text-right font-mono">{fmtMoney(totals.vat, currency)}</td>
                    </tr>
                    <tr className="border-t-2 border-emerald-500/30">
                      <td colSpan={5} className="px-3 py-2 text-right font-semibold">Total factură (cu TVA)</td>
                      <td data-testid="invoice-preview-total" className="px-3 py-2 text-right font-mono text-base font-bold text-emerald-200">{fmtMoney(totals.gross, currency)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div className="grid grid-cols-2 gap-2 text-[11px] text-zinc-500">
                <div className="rounded-md border border-zinc-800 bg-zinc-900/40 px-2.5 py-1.5">
                  <p className="text-[9px] uppercase tracking-wider text-zinc-600">Gestiune</p>
                  <p className="text-zinc-300">{preview.management || "—"}</p>
                </div>
                <div className="rounded-md border border-zinc-800 bg-zinc-900/40 px-2.5 py-1.5">
                  <p className="text-[9px] uppercase tracking-wider text-zinc-600">Platforma · CIF</p>
                  <p className="text-zinc-300">{(preview.platform || "").toUpperCase()} · <span className="font-mono">{preview.cif}</span></p>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-zinc-800 bg-zinc-900/40 px-4 py-3">
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            data-testid="invoice-upload-input"
            onChange={(e) => handleUpload(e.target.files?.[0])}
          />
          <button
            data-testid="invoice-upload-btn"
            onClick={() => fileRef.current?.click()}
            disabled={uploading || emitting}
            title="Încarcă un PDF emis extern — se atașează ca o factură generată de noi (email client, push Shopify, finalizare)"
            className="mr-auto inline-flex items-center gap-1.5 rounded-md border border-sky-500/40 bg-sky-500/10 px-3 py-2 text-xs font-medium text-sky-200 hover:bg-sky-500/20 disabled:opacity-50"
          >
            {uploading ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Se încarcă...</> : <><Upload className="h-3.5 w-3.5" /> Upload PDF</>}
          </button>
          <button onClick={onClose} disabled={emitting || uploading} className="rounded-md border border-zinc-700 bg-zinc-900 px-4 py-2 text-sm text-zinc-300 hover:border-zinc-600 disabled:opacity-50">Anulează</button>
          <button data-testid="invoice-emit-btn" onClick={handleEmit} disabled={emitting || uploading || loading || !preview}
            className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500 px-4 py-2 text-sm font-medium text-zinc-950 hover:bg-emerald-400 disabled:opacity-50">
            {emitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Se emite...</> : <><CheckCircle2 className="h-4 w-4" /> Emite factura</>}
          </button>
        </div>
      </div>
    </div>
  );
}
